import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Container, Typography, useMediaQuery } from '@mui/material';
import Navbar from './components/Navbar';
import AnnouncementBanner from './components/AnnouncementBanner';
import ProviderQuickAccessSidebar from './components/ProviderQuickAccessSidebar';
import AdminQuickAccessSidebar from './components/AdminQuickAccessSidebar';
import { PROVIDER_SIDEBAR_MIN_WIDTH_PX } from './config/providerNavItems';
import { ADMIN_SIDEBAR_MIN_WIDTH_PX } from './config/adminNavItems';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import UserTypeSelectionPage from './pages/UserTypeSelectionPage';
import ProviderDashboard from './pages/ProviderDashboard';
import ProviderPerformancePage from './pages/ProviderPerformancePage';
import ProviderEarningsPage from './pages/ProviderEarningsPage';
import ProviderFinancialDashboardPage from './pages/ProviderFinancialDashboardPage';
import ProviderAdditionalServicesPage from './pages/ProviderAdditionalServicesPage';
import ProviderCrewManagementPage from './pages/ProviderCrewManagementPage';
import ProviderServiceHistoryPage from './pages/ProviderServiceHistoryPage';
import ProviderRenewalsPage from './pages/ProviderRenewalsPage';
import ProviderTutorialsPage from './pages/ProviderTutorialsPage';
import ProviderCustomerAgreementsPage from './pages/ProviderCustomerAgreementsPage';
import PayoutsPage from './pages/PayoutsPage';
import ServiceRequestPage from './pages/ServiceRequestPage';
import JobDetailsPage from './pages/JobDetailsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import StripeOnboardingReturnPage from './pages/StripeOnboardingReturnPage';
import StripeOnboardingRefreshPage from './pages/StripeOnboardingRefreshPage';
import MyRequestsPage from './pages/MyRequestsPage';
import CustomerDashboard from './pages/CustomerDashboard';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import WebsiteSettingsPage from './pages/WebsiteSettingsPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminFinancialsPage from './pages/AdminFinancialsPage';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminServiceSearchPage from './pages/AdminServiceSearchPage';
import AdminCancellationsPage from './pages/AdminCancellationsPage';
import AdminOpenServiceRequestsPage from './pages/AdminOpenServiceRequestsPage';
import AdminPaymentsRequiringAttentionPage from './pages/AdminPaymentsRequiringAttentionPage';
import CompliancePage from './pages/CompliancePage';
import AdminProviderSavingsLeadsPage from './pages/AdminProviderSavingsLeadsPage';
import AdminAgentsPage from './pages/AdminAgentsPage';
import AdminDemoRequestsPage from './pages/AdminDemoRequestsPage';
import RequestDemoPage from './pages/RequestDemoPage';
import DemoExitPage from './pages/DemoExitPage';
import DemoScheduleCallPage from './pages/DemoScheduleCallPage';
import DemoSessionBanner from './components/DemoSessionBanner';
import GoodFaithPaymentPage from './pages/GoodFaithPaymentPage';
import CrewLoginPage from './pages/CrewLoginPage';
import CrewDashboard from './pages/CrewDashboard';
import CrewJobDetailsPage from './pages/CrewJobDetailsPage';
import InvitationSignupPage from './pages/InvitationSignupPage';
import RateServicePage from './pages/RateServicePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import W9UploadPage from './pages/W9UploadPage';
import PesticideLicenseUploadPage from './pages/PesticideLicenseUploadPage';
import AboutUsPage from './pages/AboutUsPage';
import ProviderBenefitsPage from './pages/ProviderBenefitsPage';
import CustomerBenefitsPage from './pages/CustomerBenefitsPage';
import LawnCareSchedulingSoftwarePage from './pages/LawnCareSchedulingSoftwarePage';
import LawnCareCrmPage from './pages/LawnCareCrmPage';
import JobberAlternativePage from './pages/JobberAlternativePage';
import HowItWorksPage from './pages/HowItWorksPage';
import FaqsPage from './pages/FaqsPage';
import LegalPage from './pages/LegalPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const ProtectedRoute: React.FC<{ children: React.ReactNode; userType?: 'customer' | 'provider' | 'admin' | 'crew' }> = ({ 
  children, 
  userType 
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    const target = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(target)}`} replace />;
  }

  if (userType && user.userType.toLowerCase() !== userType.toLowerCase()) {
    return <WrongAccountNotice requiredType={userType} />;
  }

  return <>{children}</>;
};

/** Shown when a link (e.g. from a text or email) is meant for a different kind of account than the one signed in. */
const WrongAccountNotice: React.FC<{ requiredType: string }> = ({ requiredType }) => {
  const { user, logout, getUserDashboardRoute } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  if (!user) return null;
  const target = `${location.pathname}${location.search}${location.hash}`;
  const required = requiredType.toLowerCase();
  const current = user.userType.toLowerCase();
  const withArticle = (word: string) => `${/^[aeiou]/.test(word) ? 'an' : 'a'} ${word}`;

  const switchAccount = async () => {
    await logout();
    navigate(`/login?redirect=${encodeURIComponent(target)}`, { replace: true });
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        This page is for {withArticle(required)} account. You&apos;re signed in as {withArticle(current)}
        {user.email ? ` (${user.email})` : ''}.
      </Alert>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Sign out and sign in with your {required} account to continue. You&apos;ll come right back to this page.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={switchAccount}>
          Sign out and switch account
        </Button>
        <Button variant="outlined" onClick={() => navigate(getUserDashboardRoute(user), { replace: true })}>
          Go to my {current} dashboard
        </Button>
      </Box>
    </Container>
  );
};

const AppRoutes: React.FC = () => {
  const { user, getUserDashboardRoute } = useAuth();
  const isTabletOrUp = useMediaQuery(
    `(min-width:${Math.max(PROVIDER_SIDEBAR_MIN_WIDTH_PX, ADMIN_SIDEBAR_MIN_WIDTH_PX)}px)`
  );
  const showProviderSidebar = user?.userType?.toLowerCase() === 'provider' && isTabletOrUp;
  const showAdminSidebar = user?.userType?.toLowerCase() === 'admin' && isTabletOrUp;

  // Redirect authenticated users from home page to their dashboard
  const HomePageWrapper = () => {
    if (user) {
      const dashboardRoute = getUserDashboardRoute(user);
      return <Navigate to={dashboardRoute} replace />;
    }
    return <HomePage />;
  };

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden', width: '100%' }}>
      {user?.userType !== 'crew' && (
        <>
          <AnnouncementBanner />
          <DemoSessionBanner />
          <Navbar />
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          minWidth: 0,
          width: '100%',
        }}
      >
        {showProviderSidebar && <ProviderQuickAccessSidebar />}
        {showAdminSidebar && <AdminQuickAccessSidebar />}
        <Box sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden' }}>
      <Routes>
        <Route path="/" element={<HomePageWrapper />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/get-started" element={<UserTypeSelectionPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/invitation/:token" element={<InvitationSignupPage />} />
        <Route path="/invitation-signup" element={<InvitationSignupPage />} />
        <Route path="/rate/:serviceRequestId" element={<RateServicePage />} />
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacy-policy" element={<LegalPage />} />
        <Route path="/provider-benefits" element={<ProviderBenefitsPage />} />
        <Route path="/request-demo" element={<RequestDemoPage />} />
        <Route path="/demo/exit" element={<DemoExitPage />} />
        <Route path="/demo/schedule-call" element={<DemoScheduleCallPage />} />
        <Route path="/customer-benefits" element={<CustomerBenefitsPage />} />
        <Route path="/lawn-care-scheduling-software" element={<LawnCareSchedulingSoftwarePage />} />
        <Route path="/lawn-care-crm" element={<LawnCareCrmPage />} />
        <Route path="/jobber-alternative" element={<JobberAlternativePage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/faqs" element={<FaqsPage />} />
        
        {/* Crew Routes */}
        <Route path="/crew/login" element={<CrewLoginPage />} />
        <Route 
          path="/crew/dashboard" 
          element={
            <ProtectedRoute userType="crew">
              <CrewDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/crew/jobs/:jobId" 
          element={
            <ProtectedRoute userType="crew">
              <CrewJobDetailsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/customer" 
          element={
            <ProtectedRoute userType="customer">
              <CustomerDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/performance" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderPerformancePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/earnings" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderEarningsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/financial-dashboard" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderFinancialDashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/additional-services" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderAdditionalServicesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/crew-management" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderCrewManagementPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/service-history" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderServiceHistoryPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/renewals" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderRenewalsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/tutorials" 
          element={
            <ProtectedRoute userType="provider">
              <ProviderTutorialsPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/provider/customer-agreements"
          element={
            <ProtectedRoute userType="provider">
              <ProviderCustomerAgreementsPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/provider/w9-upload" 
          element={
            <ProtectedRoute userType="provider">
              <W9UploadPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/pesticide-license-upload" 
          element={
            <ProtectedRoute userType="provider">
              <PesticideLicenseUploadPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/request-service" 
          element={
            <ProtectedRoute userType="customer">
              <ServiceRequestPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/job/:jobId" 
          element={
            <ProtectedRoute>
              <JobDetailsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/stripe/return" 
          element={
            <ProtectedRoute>
              <StripeOnboardingReturnPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/provider/stripe/refresh" 
          element={
            <ProtectedRoute>
              <StripeOnboardingRefreshPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute userType="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/open-service-requests"
          element={
            <ProtectedRoute userType="admin">
              <AdminOpenServiceRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/admin/financials"
          element={
            <ProtectedRoute userType="admin">
              <AdminFinancialsPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/admin/website-settings" 
          element={
            <ProtectedRoute userType="admin">
              <WebsiteSettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/payouts" 
          element={
            <ProtectedRoute userType="admin">
              <PayoutsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/reports" 
          element={
            <ProtectedRoute userType="admin">
              <AdminReportsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/services/search" 
          element={
            <ProtectedRoute userType="admin">
              <AdminServiceSearchPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/cancellations" 
          element={
            <ProtectedRoute userType="admin">
              <AdminCancellationsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/payments-requiring-attention" 
          element={
            <ProtectedRoute userType="admin">
              <AdminPaymentsRequiringAttentionPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/good-faith-payment" 
          element={
            <ProtectedRoute userType="admin">
              <GoodFaithPaymentPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/compliance" 
          element={
            <ProtectedRoute userType="admin">
              <CompliancePage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/admin/provider-savings-leads"
          element={
            <ProtectedRoute userType="admin">
              <AdminProviderSavingsLeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agents"
          element={
            <ProtectedRoute userType="admin">
              <AdminAgentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/demo-requests"
          element={
            <ProtectedRoute userType="admin">
              <AdminDemoRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/my-requests" 
          element={
            <ProtectedRoute userType="customer">
              <MyRequestsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment-success" 
          element={
            <ProtectedRoute userType="customer">
              <PaymentSuccessPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment-cancel" 
          element={
            <ProtectedRoute userType="customer">
              <PaymentCancelPage />
            </ProtectedRoute>
          } 
        />
      </Routes>
        </Box>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;
